import axios from 'axios';

enum DataType {
  LAMINAS_API = 'LAMINAS_API',
  TAX_REPORTS_API = 'TAX_REPORTS_API',
  MAIN_APP_API = 'MAIN_APP_API',
  MOCK = 'MOCK',
}

const postData = async <T, D>(
  url: string,
  data: D,
  dataType: DataType,
  signal?: AbortSignal,
  headers?: object,
): Promise<T> => {
  try {
    let baseURL;
    switch (dataType) {
      case DataType.LAMINAS_API:
        baseURL = process.env.LAMINAS_API_URL;
        break;
      case DataType.TAX_REPORTS_API:
        baseURL = process.env.TAX_REPORTS_API_URL;
        break;
      case DataType.MAIN_APP_API:
        baseURL = process.env.MAIN_APP_API_URL;
        break;
      case DataType.MOCK:
        baseURL = process.env.MOCK_API_URL;
        break;
      default:
        baseURL = '';
        break;
    }
    const res = await axios.post<T>(url, data, {baseURL, signal: signal, headers: headers, withCredentials: true});
    const apiRes = res.data;
    if (([DataType.LAMINAS_API, DataType.TAX_REPORTS_API, DataType.MAIN_APP_API]
      .includes(dataType)) && apiRes) {
      return apiRes;
    } if ((dataType === DataType.MOCK) && apiRes) {
      return apiRes as unknown as T;
    }
    throw Error('Something went wrong');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    throw error;
  }
};

const getData = async <T>(
  url: string,
  dataType: DataType,
  signal?: AbortSignal,
): Promise<T> => {
  try {
    let baseURL;
    switch (dataType) {
      case DataType.LAMINAS_API:
        baseURL = process.env.LAMINAS_API_URL;
        break;
      case DataType.TAX_REPORTS_API:
        baseURL = process.env.TAX_REPORTS_API_URL;
        break;
      case DataType.MAIN_APP_API:
        baseURL = process.env.MAIN_APP_API_URL;
        break;
      case DataType.MOCK:
        baseURL = process.env.MOCK_API_URL;
        break;
      default:
        baseURL = '';
        break;
    }

    const res = await axios.get<T>(url, {
      baseURL,
      withCredentials: true,
      signal,
    });
    const apiRes = res.data;

    if (([DataType.LAMINAS_API, DataType.TAX_REPORTS_API, DataType.MAIN_APP_API]
      .includes(dataType)) && apiRes) {
      return apiRes;
    }

    if ((dataType === DataType.MOCK) && apiRes) {
      return apiRes;
    }

    throw Error('Something went wrong');
  } catch (error) {
    if (error instanceof Error && error.name && error.name !== 'CanceledError') {
      // eslint-disable-next-line no-console
      console.error(error);
      throw error;
    } else {
      // eslint-disable-next-line no-console
      console.error('Request cancelled');
      throw error;
    }
  }
};

export const getLaminasApiData = async <T>(
  url: string,
  signal?: AbortSignal,
): Promise<T> => getData(url, DataType.LAMINAS_API, signal);

export const postLaminasApiData = async <T, D>(
  url: string,
  data: D,
  signal?: AbortSignal,
  headers?: object,
): Promise<T> => postData(url, data, DataType.LAMINAS_API, signal, headers);

export const getTaxReportsApiData = async <T>(
  url: string,
  signal?: AbortSignal,
): Promise<T> => getData(url, DataType.TAX_REPORTS_API, signal);

export const postTaxReportsApiData = async <T, D>(
  url: string,
  data: D,
): Promise<T> => postData(url, data, DataType.TAX_REPORTS_API);

export const getMainAppApiData = async <T>(
  url: string,
  signal?: AbortSignal,
): Promise<T> => getData(url, DataType.MAIN_APP_API, signal);

export const postMainAppApiData = async <T, D>(
  url: string,
  data: D,
): Promise<T> => postData(url, data, DataType.MAIN_APP_API);

export const getMockData = async <T>(
  url: string,
): Promise<T> => getData(url, DataType.MOCK);

export const postMockData = async <T, D>(
  url: string,
  data: D,
): Promise<T> => postData(url, data, DataType.MOCK);

export const serializeTaxReportBody = function (obj) {
  const str = [];
  for (const p in obj) {
    if (obj.hasOwnProperty(p)) {
      str.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
    }
  }
  return str.join('&');
};
